/**
 * Facebook Login config — super-admin pastes App ID + App Secret for the
 * "Continue with Facebook" button on /login and /signup. Stored in Mongo
 * (`app_config._id = "facebook_oauth"`) — no env vars or redeploy needed.
 */
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Facebook, Save, CheckCircle2, XCircle, ExternalLink, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthProviderToggleRow } from "./AuthProviderToggleRow";

export const FacebookLoginConfigCard: React.FC = () => {
  const qc = useQueryClient();
  const cfg = useQuery<{ app_id: string; app_secret_set: boolean }>({
    queryKey: ["fb-login-config"],
    queryFn: async () => (await api.get("/auth/admin/facebook-config")).data,
  });

  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    if (cfg.data?.app_id) setAppId(cfg.data.app_id);
  }, [cfg.data?.app_id]);

  const save = useMutation({
    mutationFn: async () => {
      const patch: any = { app_id: appId.trim() };
      if (appSecret.trim()) patch.app_secret = appSecret.trim();
      return (await api.put("/auth/admin/facebook-config", patch)).data;
    },
    onSuccess: () => {
      toast.success("Facebook Login credentials saved · users can now log in with Facebook");
      setAppSecret("");
      qc.invalidateQueries({ queryKey: ["fb-login-config"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const configured = !!cfg.data?.app_id && !!cfg.data?.app_secret_set;
  const originHint = typeof window !== "undefined" ? window.location.hostname : "";

  return (
    <div className="sg-card p-6" data-testid="facebook-login-config-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center shrink-0">
          <Facebook className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold text-[color:var(--sg-fg)]">Facebook Login</div>
          <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
            The "Continue with Facebook" button on <code>/login</code> and <code>/signup</code>. Keys stored in Mongo — production doesn't need env vars.
          </div>
        </div>
        {configured ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900" data-testid="fb-login-status">
            <CheckCircle2 className="h-3 w-3" /> Live
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-200 dark:border-rose-900" data-testid="fb-login-status">
            <XCircle className="h-3 w-3" /> Not configured
          </span>
        )}
      </div>

      <AuthProviderToggleRow provider="facebook" label="Facebook Login" />

      <div className="space-y-4">
        <div>
          <Label htmlFor="fb-app-id" className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
            App ID
          </Label>
          <Input
            id="fb-app-id"
            data-testid="fb-login-app-id"
            placeholder="1234567890"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            className="mt-1 font-mono text-xs"
          />
        </div>

        <div>
          <Label htmlFor="fb-app-secret" className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
            App Secret {cfg.data?.app_secret_set && <span className="text-[10px] font-normal ml-1 text-emerald-600 dark:text-emerald-400">· saved (paste to replace)</span>}
          </Label>
          <div className="relative mt-1">
            <Input
              id="fb-app-secret"
              data-testid="fb-login-app-secret"
              type={showSecret ? "text" : "password"}
              placeholder={cfg.data?.app_secret_set ? "•••••••••••••••••••••••" : "abcdef1234567890…"}
              value={appSecret}
              onChange={(e) => setAppSecret(e.target.value)}
              className="font-mono text-xs pr-16"
            />
            <button
              type="button"
              onClick={() => setShowSecret((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]"
            >
              {showSecret ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        <Button
          onClick={() => save.mutate()}
          disabled={save.isPending || !appId.trim()}
          data-testid="fb-login-save-btn"
          className="bg-[color:var(--sg-fg)] text-white hover:bg-black dark:bg-white dark:text-[color:var(--sg-fg)] dark:hover:bg-slate-200"
        >
          {save.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save credentials
        </Button>
      </div>

      <div className="mt-5 pt-5 border-t border-[color:var(--sg-border)]">
        <div className="text-xs font-semibold text-[color:var(--sg-fg)] mb-2">Meta / Facebook app setup</div>
        <ol className="text-xs text-[color:var(--sg-muted)] space-y-1.5 list-decimal pl-5">
          <li>
            Open <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-[color:var(--sg-primary)] hover:underline inline-flex items-center gap-1">Meta for Developers → My Apps <ExternalLink className="h-3 w-3" /></a>, create a Consumer app.
          </li>
          <li>Under <b>Settings → Basic</b>, copy the <b>App ID</b> and <b>App Secret</b> and paste them above.</li>
          <li>Add the <b>Facebook Login</b> product to your app.</li>
          <li>
            Under <b>Facebook Login → Settings</b>, set <b>Login with the JavaScript SDK</b> = <b>Yes</b> and add these <b>Allowed Domains</b>:
            <div className="mt-1.5 space-y-1">
              <code className="block px-2 py-1 rounded bg-[color:var(--sg-muted-bg)] text-[11px]">{originHint}</code>
              <code className="block px-2 py-1 rounded bg-[color:var(--sg-muted-bg)] text-[11px]">socibaba.com</code>
              <code className="block px-2 py-1 rounded bg-[color:var(--sg-muted-bg)] text-[11px]">www.socibaba.com</code>
            </div>
          </li>
          <li>Requested scopes: <code>public_profile</code>, <code>email</code> — both available without app review.</li>
          <li>Move the app to <b>Live</b> mode when you're ready for real users.</li>
          <li>Reload <code>/login</code> — the "Continue with Facebook" button will appear.</li>
        </ol>
      </div>
    </div>
  );
};

export default FacebookLoginConfigCard;
